import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';

const socials = [
  { name: 'Facebook', url: 'https://www.facebook.com/sparky.advisor', label: "Thomas White's Business Tips", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
  { name: 'Instagram', url: 'https://www.instagram.com/sparky.advisor', icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
  { name: 'LinkedIn', url: 'https://www.linkedin.com/company/wombex/', icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
  { name: 'Wombex', url: 'https://web.wombex.com', icon: <img src="/wombex-logo.png" alt="Wombex" className="w-5 h-5 rounded" /> },
];

export default function Footer() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) { toast.error('Please fill all fields'); return; }
    setSending(true);
    try {
      await api.post('/public/contact', { name, email, message });
      toast.success('Message sent! We\'ll get back to you soon.');
      setSent(true);
      setName(''); setEmail(''); setMessage('');
    } catch { toast.error('Failed to send message'); }
    setSending(false);
  };

  return (
    <footer className="border-t bg-card" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="Wayfinder" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-lg font-['Outfit']">Wayfinder Guides</span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Real advice from someone who figured it out the hard way. Short audio guides for small business owners.
            </p>
            <div className="flex items-center gap-3">
              {socials.map(s => (
                <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" title={s.label || s.name} className="text-muted-foreground hover:text-[#1A9DA5] transition-colors">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-semibold text-sm font-['Outfit'] mb-4">Quick Links</h3>
            <div className="space-y-2">
              <Link to="/" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Browse Guides</Link>
              <Link to="/portal" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">My Library</Link>
              <Link to="/affiliate" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Affiliate Program</Link>
              <a href="https://www.facebook.com/sparky.advisor" target="_blank" rel="noopener noreferrer" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Thomas White's Business Tips</a>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h3 className="font-semibold text-sm font-['Outfit'] mb-4">Get in Touch</h3>
            {sent ? (
              <div className="p-4 rounded-lg bg-[#1A9DA5]/10 border border-[#1A9DA5]/20 text-center">
                <p className="text-sm font-medium text-[#1A9DA5]">Message sent!</p>
                <p className="text-xs text-muted-foreground mt-1">We'll get back to you soon.</p>
                <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={() => setSent(false)}>Send another</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-2">
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="h-8 text-sm" data-testid="contact-name" />
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email" className="h-8 text-sm" data-testid="contact-email" />
                <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Your message..." rows={3} className="text-sm" data-testid="contact-message" />
                <Button type="submit" disabled={sending} size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white w-full" data-testid="contact-submit">
                  {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-10 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src="/wombex-logo.png" alt="Wombex" className="w-5 h-5 rounded" />
            <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Wombex UK Ltd. & Thomas White. All rights reserved.</p>
          </div>
          <div className="flex items-center gap-3">
            {socials.map(s => (
              <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#1A9DA5] transition-colors">
                {React.cloneElement(s.icon, { className: 'w-4 h-4' })}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
